import { useRef, useState } from 'react';
import { Alert, Form, Input, Modal } from 'antd';
import { useMsal } from '@azure/msal-react';
import type { SalesCall1LeadContact, SalesCall1LeadContactInfo, CaseInfo } from '../../../dto/model';
import { strings } from '../../../lang';
import { GetAccessToken } from '../../../utils/auth-utils';
import { scopes } from '../../../authConfig';
import { heatBookingPageService } from '../../../services/heat-booking-page-service';

type Props = {
  caseInfo: CaseInfo;
  disabled?: boolean;
  onCancel: () => void;
  onSaved: (contact: SalesCall1LeadContactInfo) => void;
  onBusyChange?: (busy: boolean) => void;
};

// Agents see only the last three digits of the stored number (LEAD-503); separators are dropped so none of them hides a digit.
const maskPhoneNumber = (phone?: string) => {
  const digits = phone?.replace(/\D/g, '') ?? '';
  return '*'.repeat(Math.max(digits.length - 3, 0)) + digits.slice(-3);
};

export const SalesCall1LeadContactEditModal = ({ caseInfo, disabled, onCancel, onSaved, onBusyChange }: Props) => {
  const { instance, inProgress } = useMsal();
  const [form] = Form.useForm<SalesCall1LeadContact>();
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [error, setError] = useState('');
  // The stored number is shown masked. It is replaced, never edited, and saved unchanged when untouched.
  const [phoneEditing, setPhoneEditing] = useState(false);
  const startPhoneEdit = () => {
    if (phoneEditing) return;
    setPhoneEditing(true);
    form.setFieldValue('phoneNumber', '');
  };
  const stopPhoneEdit = () => {
    if (!phoneEditing || form.getFieldValue('phoneNumber')?.trim()) return;
    setPhoneEditing(false);
    form.setFields([{ name: 'phoneNumber', value: maskPhoneNumber(caseInfo.phone), errors: [] }]);
  };
  const save = async ({ phoneNumber, ...values }: SalesCall1LeadContact) => {
    if (savingRef.current || disabled) return;
    savingRef.current = true;
    setSaving(true);
    setError('');
    onBusyChange?.(true);
    try {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const result = await heatBookingPageService.updateSalesCall1LeadContact(caseInfo.internalId,
        { ...values, phoneNumber: phoneEditing ? phoneNumber : caseInfo.phone }, token.accessToken);
      if (!result.success || !result.data) throw new Error(result.error || strings.adBookingPage.contactSaveError);
      onSaved(result.data);
    } catch {
      setError(strings.adBookingPage.contactSaveError);
    } finally {
      savingRef.current = false;
      setSaving(false);
      onBusyChange?.(false);
    }
  };

  return <Modal open title={strings.contactEditTitle} onCancel={() => { if (!savingRef.current) onCancel(); }}
    onOk={() => form.submit()} okText={error ? strings.adBookingPage.contactRetry : strings.contactEditSave}
    cancelText={strings.contactEditCancel} maskClosable={false}
    confirmLoading={saving} okButtonProps={{ disabled: disabled || saving }}
    cancelButtonProps={{ disabled: saving }} closable={!saving} keyboard={!saving}>
    {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
    <Form form={form} layout="vertical" onFinish={save} disabled={saving || disabled}
      initialValues={{ firstName: caseInfo.firstName, lastName: caseInfo.lastName, email: caseInfo.email, phoneNumber: maskPhoneNumber(caseInfo.phone) }}>
      <Form.Item name="firstName" label={strings.contactEditFirstName}
        rules={[{ required: true, whitespace: true, message: strings.adBookingPage.contactRequired }, { max: 128 }]}>
        <Input autoComplete="given-name" />
      </Form.Item>
      <Form.Item name="lastName" label={strings.contactEditLastName}
        rules={[{ required: true, whitespace: true, message: strings.adBookingPage.contactRequired }, { max: 128 }]}>
        <Input autoComplete="family-name" />
      </Form.Item>
      <Form.Item name="email" label={strings.preSalesQuestionnaire.emailLabel}
        rules={[{ required: true, message: strings.adBookingPage.contactRequired }, { type: 'email', message: strings.adBookingPage.contactEmailInvalid }, { max: 256 }]}>
        <Input type="email" autoComplete="email" />
      </Form.Item>
      <Form.Item name="phoneNumber" label={strings.contactEditPhone}
        rules={[{ required: true, message: strings.adBookingPage.contactRequired },
          ...(phoneEditing ? [{ pattern: /^\+?[\d\s\-()]{7,20}$/, message: strings.adBookingPage.contactPhoneInvalid }] : [])]}>
        <Input type="tel" autoComplete={phoneEditing ? 'tel' : 'off'} onFocus={startPhoneEdit} onBlur={stopPhoneEdit} />
      </Form.Item>
    </Form>
  </Modal>;
};
